import React, { useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { SaveState } from '../../../../components/SaveDot';
import { formatHoursShort, formatMinutes, LunchBreak } from '../../../../utils/timesheetTime';
import { TimesheetEntry, TimesheetEntryInput, TimesheetEntryPatch } from '../../config';
import { dayRows, lastEndTime, lunchClash, sumHours } from '../../timesheetDerive';
import DayEntryRow from './DayEntryRow';
import DayAddRow, { DayAddSeed } from './DayAddRow';
import DayLunchRow, { LunchField } from './DayLunchRow';
import { useFillToPageBottom } from '../../useFillToPageBottom';
import './day-view.scss';

interface DayViewProps {
  date: string;
  entries: TimesheetEntry[];
  isReadOnly: boolean;
  standardHours: number;
  /** "HH:mm" the first entry of a day starts at, from the employee's profile. */
  defaultStart: string;
  /** This day's lunch break; timed entries lose what falls inside it. */
  lunch: LunchBreak;
  /** The employee's usual lunch break, which the day has unless it was changed. */
  usualLunch: LunchBreak;
  /** True when this day has its own lunch rather than the usual one. */
  lunchIsCustom: boolean;
  lunchSaveState: (field: LunchField) => SaveState;
  /** null goes back to the usual lunch. */
  onLunchChange: (field: LunchField, next: LunchBreak | null) => void;
  saveState: (entryId: number, field: string) => SaveState;
  onPatch: (entry: TimesheetEntry, field: string, patch: TimesheetEntryPatch) => void;
  onCreate: (input: TimesheetEntryInput) => Promise<void>;
  onEdit: (entry: TimesheetEntry) => void;
  onDuplicate: (entry: TimesheetEntry) => void;
  onDelete: (entry: TimesheetEntry) => void;
  onProjectClick?: (entry: TimesheetEntry) => void;
  addSeed: DayAddSeed | null;
  onAddSeedConsumed: () => void;
}

/**
 * One day as a timeline: timed entries in order with the lunch break and the
 * gaps between them called out, then untimed entries, then the add row.
 * Editable in place.
 */
const DayView: React.FC<DayViewProps> = ({
  date,
  entries,
  isReadOnly,
  standardHours,
  defaultStart,
  lunch,
  usualLunch,
  lunchIsCustom,
  lunchSaveState,
  onLunchChange,
  saveState,
  onPatch,
  onCreate,
  onEdit,
  onDuplicate,
  onDelete,
  onProjectClick,
  addSeed,
  onAddSeedConsumed
}) => {
  const rows = dayRows(entries, lunch);
  const clash = lunchClash(entries, lunch);
  const total = sumHours(entries);
  const lastEnd = lastEndTime(entries);
  const columns = isReadOnly ? 6 : 7;
  const wrap = useRef<HTMLDivElement>(null);
  useFillToPageBottom(wrap);

  return (
    <div className="day-view" ref={wrap}>
      <table className="ui-table day-view__table">
        <thead>
          <tr>
            <th className="day-view__dot-col" />
            <th className="day-view__time-col">Start</th>
            <th className="day-view__time-col">End</th>
            <th className="day-view__hours-col">Hours</th>
            <th className="day-view__project-col">Project</th>
            <th className="day-view__notes-col">Notes</th>
            {!isReadOnly && <th className="ui-table__actions" />}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr className="day-view__empty">
              <td colSpan={columns}>
                <p className="ui-table__empty">
                  Nothing on {format(parseISO(date), 'EEEE d MMMM')} yet.
                  {!isReadOnly && ' Start in the row below, or pick a recent project above.'}
                </p>
              </td>
            </tr>
          )}
          {rows.map((row) =>
            row.kind === 'gap' ? (
              <tr key={row.key} className="day-view__gap">
                <td />
                <td colSpan={columns - 1}>
                  <span className="day-view__gap-label">{formatMinutes(row.minutes)} gap</span>
                </td>
              </tr>
            ) : row.kind === 'lunch' ? (
              <DayLunchRow
                key={row.key}
                lunch={lunch}
                usual={usualLunch}
                isCustom={lunchIsCustom}
                clash={clash}
                isReadOnly={isReadOnly}
                columns={columns}
                saveState={lunchSaveState}
                onChange={onLunchChange}
              />
            ) : (
              <DayEntryRow
                key={row.entry.id}
                entry={row.entry}
                overlaps={row.overlaps}
                isReadOnly={isReadOnly}
                lunch={lunch}
                saveState={(field) => saveState(row.entry.id, field)}
                onPatch={onPatch}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                onProjectClick={onProjectClick}
              />
            )
          )}
          {!isReadOnly && (
            // Keyed on the date so switching days starts a fresh row (at that
            // day's own start) instead of carrying over what was half-typed.
            <DayAddRow key={date} date={date} lastEnd={lastEnd} defaultStart={defaultStart} lunch={lunch} seed={addSeed} onCreate={onCreate} onSeedConsumed={onAddSeedConsumed} />
          )}
        </tbody>
        <tfoot>
          <tr className="day-view__totals">
            <td />
            <td colSpan={2} className="day-view__totals-label">
              Day total
            </td>
            <td className="day-view__hours-col">
              <span className={`day-view__hours is-total ${total >= standardHours ? 'is-met' : total > 0 ? 'is-short' : ''}`}>
                {formatHoursShort(total)}
              </span>
            </td>
            <td colSpan={columns - 4} className="day-view__totals-target">
              of {formatHoursShort(standardHours)} standard
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default DayView;
